import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';
import s from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'light' | 'danger' | 'outline';
export type ButtonSize = 'cta' | 46 | 42 | 38 | 36 | 34 | 32;

const SIZE: Record<string, string | undefined> = {
  cta: s.cta,
  46: s.s46,
  42: s.s42,
  38: s.s38,
  36: s.s36,
  34: s.s34,
  32: s.s32,
};

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon (the markup's lu-icon before the label). */
  icon?: IconName;
  iconSize?: number;
  /** Trailing icon (e.g. «Перейти к структуре →»). */
  trailingIcon?: IconName;
  block?: boolean;
  /** Label left, trailing icon right (the variant cards' «Открыть в AI Designer →»). */
  spread?: boolean;
  children?: ReactNode;
};

/** Pill button: primary (orange), secondary (outline), light, danger-outline, outline (header/toolbar). */
export function Button({
  variant = 'primary',
  size = 42,
  icon,
  iconSize = 16,
  trailingIcon,
  block,
  spread,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(s.btn, s[variant], SIZE[String(size)], block && s.block, spread && s.spread, className)}
      {...rest}
    >
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {trailingIcon && <Icon name={trailingIcon} size={iconSize} />}
    </button>
  );
}

export type IconButtonTone = 'outline' | 'header' | 'ghost' | 'primary' | 'danger';
const TONE: Record<IconButtonTone, string | undefined> = {
  outline: s.iOutline,
  header: s.iHeader,
  ghost: s.iGhost,
  primary: s.iPrimary,
  danger: s.iDanger,
};
const ISIZE: Record<number, string | undefined> = { 30: s.i30, 32: s.i32, 34: s.i34, 36: s.i36, 38: s.i38, 40: s.i40 };

type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  icon: IconName;
  /** Accessible name; also the tooltip (the markup's title). */
  label: string;
  tone?: IconButtonTone;
  size?: 30 | 32 | 34 | 36 | 38 | 40;
  iconSize?: number;
  children?: ReactNode;
};

/** Round icon button (DESIGN_SPEC §3 «icon-circle», 30–40px). */
export function IconButton({
  icon,
  label,
  tone = 'outline',
  size = 36,
  iconSize = 16,
  className,
  type = 'button',
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      title={label}
      aria-label={label}
      className={cx(s.iconBtn, ISIZE[size], TONE[tone], className)}
      {...rest}
    >
      <Icon name={icon} size={iconSize} />
      {children}
    </button>
  );
}
